import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { DateWithItinerary } from '@apps/main/new-desktop/core/stores/trip/tripStore.model';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import { TripPlannerPax } from '@apps/main/modules/recipe/pax';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { assertValue, exhaustiveGuard, isDefined } from '@libs/utils';
import type {
  ActivityStaticInfo,
  ActivitySuggestionsByIngredientId,
  ActivitySuggestionWithStaticInfo,
  FerryIngredientResponse,
  FlightIngredientResponse,
  HotelIngredientResponse,
  IngredientId,
  ItineraryMainIngredientResponse,
  ItinerarySuggestionPrices,
  ItineraryWithPrice,
  Money,
  OwnStayIngredientResponse,
} from '@libs/trip-planner-interfaces';
import { DateFormat, SemboDate } from '@libs/sembo-dates';
import {
  isAvailableHotelIngredientResponse,
  isFerryIngredientResponse,
  isFlightIngredientResponse,
  isHotelIngredientResponse,
  isOwnStayIngredientResponse,
  isStayIngredientResponse,
  isTransportIngredientResponse,
} from '@libs/search-service-smart-search';

import { usePreferredTimeStore } from '../preferredTimeStore';

type ItineraryAvailabilitySearchCompletionStatus =
  | 'pending'
  | 'complete'
  | 'error';

interface ItineraryAvailabilitySearchCompletion {
  generation: number;
  status: ItineraryAvailabilitySearchCompletionStatus;
  isCurrentSearchResult: boolean;
  hasAvailability: boolean;
  isAvailabilityFailure: boolean;
}

export const useItineraryStore = defineStore('itineraryStore', () => {
  const { t } = i18n.global;
  const preferredTimeStore = usePreferredTimeStore();

  // State
  const cheapestItinerary = ref<ItineraryWithPrice>();
  const itineraryDates = ref<DateWithItinerary[]>([]);
  const itinerarySearchCounter = ref(0);
  const staticItinerarySearchCounter = ref(0);
  const itinerarySearchGeneration = ref(0);
  const latestAvailabilitySearchCompletion =
    ref<ItineraryAvailabilitySearchCompletion>();
  const activitySuggestions = ref<ActivitySuggestionsByIngredientId>({});
  const suggestedActivitiesByDestination = ref<
    Map<IngredientId, ActivityStaticInfo[]>
  >(new Map());
  const globalSuggestionItinerariesCounter = ref<number>(0);

  const mostPopularItinerarySuggestionPrice = ref<Money | null>(null);
  const cheapestItinerarySuggestionPrice = ref<Money | null>(null);

  // Getters
  const itineraries = computed<ItineraryWithPrice[]>(() =>
    itineraryDates.value
      .map((availableItineraryDate) => availableItineraryDate.itinerary)
      .filter(isDefined)
  );

  const areWholeMonthItinerariesSearched = computed<boolean>(
    () =>
      !!itineraries.value.length &&
      itineraries.value.every((itinerary) => itinerary.isSearched)
  );

  // TODO: Remove when SmartSearch returns itinerarySuggestion prices
  const isSearchingGlobalSuggestionItineraries = computed<boolean>(
    () => globalSuggestionItinerariesCounter.value > 0
  );

  const isSearchingItinerariesAvailability = computed(
    () => itinerarySearchCounter.value > 0
  );

  const isSearchingItineraries = computed(
    () =>
      staticItinerarySearchCounter.value > 0 ||
      isSearchingItinerariesAvailability.value
  );

  const userSelectedItinerary = computed(() => {
    const selectedDate = preferredTimeStore.preferredDay;

    if (!selectedDate) {
      return;
    }

    return itineraries.value.find((itinerary) =>
      new SemboDate(itinerary.date).isSameDay(selectedDate)
    );
  });

  const selectedItinerary = computed(() =>
    preferredTimeStore.preferredDay
      ? userSelectedItinerary.value
      : cheapestItinerary.value
  );

  const tripStartDate = computed(
    () =>
      preferredTimeStore.preferredDay ||
      (selectedItinerary.value && selectedItinerary.value.date)
  );

  const itineraryNumberOfPax = computed(() => {
    const firstAvailableHotel =
      selectedItinerary.value?.hotelOnlyItinerary.find(
        isAvailableHotelIngredientResponse
      );

    if (!firstAvailableHotel) {
      return;
    }

    return new TripPlannerPax(firstAvailableHotel.suggested.pax).totalPax;
  });

  const itineraryNumberOfNights = computed(() => {
    if (
      !selectedItinerary.value ||
      !isCompleteItinerary(selectedItinerary.value)
    ) {
      return;
    }

    return [
      ...selectedItinerary.value.hotelOnlyItinerary,
      ...selectedItinerary.value.ownStayOnlyItinerary,
    ].reduce((acc, curr) => (acc += curr.numberOfNights), 0);
  });

  const monthIsAvailable = computed(() =>
    itineraryDates.value.some((monthDate) => monthDate.itinerary)
  );

  const dateIsAvailable = computed(() => !!selectedItinerary.value);

  const itinerariesInDateAreNotAvailable = computed(
    () =>
      monthIsAvailable.value &&
      !dateIsAvailable.value &&
      !isSearchingItineraries.value
  );

  const itinerariesInMonthAreNotAvailable = computed(
    () => !monthIsAvailable.value && !isSearchingItineraries.value
  );

  const isNoAvailForMonth = computed<boolean | undefined>(() => {
    if (isSearchingItineraries.value) {
      return undefined;
    }
    const noavail = itineraries.value.every(
      (itinerary) =>
        itinerary.totalPrice === null &&
        itinerary.totalPriceWithAlternative === null
    );

    return noavail;
  });

  // Actions
  function insertItinerary(itinerary: ItineraryWithPrice): void {
    const foundDateWithItinerary = itineraryDates.value.find(
      (dateWithItinerary) =>
        new SemboDate(dateWithItinerary.date).isSameDay(itinerary.date)
    );

    if (foundDateWithItinerary) {
      foundDateWithItinerary.itinerary = itinerary;
    }
  }

  function insertItinerarySuggestionPrice(
    suggestionType: SuggestionType,
    suggestionPrices: ItinerarySuggestionPrices
  ): void {
    const foundDateWithItinerary = itineraryDates.value.find(
      (dateWithItinerary) =>
        new SemboDate(dateWithItinerary.date).isSameDay(suggestionPrices.date)
    );

    if (foundDateWithItinerary?.itinerary) {
      switch (suggestionType) {
        case SuggestionType.Cheapest:
          {
            const cheapestPrice = getSuggestionPrice(
              suggestionPrices,
              foundDateWithItinerary.itinerary
            );

            foundDateWithItinerary.itinerary.cheapestPrice = cheapestPrice;

            const suggestionAmount =
              cheapestItinerarySuggestionPrice.value?.amount;
            const cheapestAmount = cheapestPrice?.amount;

            if (
              cheapestAmount &&
              (suggestionAmount === undefined ||
                suggestionAmount > cheapestAmount)
            ) {
              cheapestItinerarySuggestionPrice.value = cheapestPrice;
            }
          }
          break;

        case SuggestionType.MostPopular:
          {
            {
              const mostPopularPrice = getSuggestionPrice(
                suggestionPrices,
                foundDateWithItinerary.itinerary
              );

              foundDateWithItinerary.itinerary.mostPopularPrice =
                mostPopularPrice;

              const suggestionAmount =
                mostPopularItinerarySuggestionPrice.value?.amount;
              const mostPopularRecommendedAmount = mostPopularPrice?.amount;

              if (
                mostPopularRecommendedAmount &&
                (suggestionAmount === undefined ||
                  suggestionAmount > mostPopularRecommendedAmount)
              ) {
                mostPopularItinerarySuggestionPrice.value = mostPopularPrice;
              }
            }
          }
          break;

        default:
          exhaustiveGuard(suggestionType, 'Invalid SuggestionType');
      }
    }

    function getSuggestionPrice(
      suggestionPrices: ItinerarySuggestionPrices,
      itinerary: ItineraryWithPrice
    ): Money | null {
      // NOTE: nullish coalescing operator fixes global price switch but not always displayng a price https://trello.com/c/n17W1BLS --> It seems to be related to the fact that slots for the activity are not always available, and the first flight for the cheapest itinerary arrives the day after. This displaces the other ingredients, so instead of receiving a total price for the itinerary, we receive a total price with alternative.
      if (itinerary.totalPrice !== null) {
        return (
          suggestionPrices.totalPrice ??
          suggestionPrices.totalPriceWithAlternative
        );
      }

      if (itinerary.totalPriceWithAlternative !== null) {
        return (
          suggestionPrices.totalPriceWithAlternative ??
          suggestionPrices.totalPrice
        );
      }
      return null;
    }
  }

  function clearItineraries(): void {
    cheapestItinerary.value = undefined;
    itineraryDates.value.forEach(
      (monthDate) => (monthDate.itinerary = undefined)
    );
  }

  function clearSingleItinerary(itineraryDate: Date): void {
    const foundItineraryDate = itineraryDates.value.find((monthDate) =>
      new SemboDate(monthDate.date).isSameDay(itineraryDate)
    );

    if (foundItineraryDate) {
      foundItineraryDate.itinerary = undefined;
    }
  }

  function toggleItinerary(itineraryDate: Date): void {
    if (
      preferredTimeStore.preferredDay &&
      new SemboDate(itineraryDate).isSameDay(preferredTimeStore.preferredDay)
    ) {
      return;
    }
    preferredTimeStore.setPreferredDay(itineraryDate);
  }

  function isCheapestItinerary(date: Date): boolean {
    return (
      !!cheapestItinerary.value &&
      new SemboDate(cheapestItinerary.value.date).isSameDay(date)
    );
  }

  function isSelectedItinerary(date: Date): boolean {
    return (
      !!preferredTimeStore.preferredDay &&
      new SemboDate(date).isSameDay(preferredTimeStore.preferredDay)
    );
  }

  function isActiveItinerary(date: Date): boolean {
    return preferredTimeStore.preferredDay
      ? isSelectedItinerary(date)
      : isCheapestItinerary(date);
  }

  function isCompleteItinerary(itinerary: ItineraryWithPrice): boolean {
    return !!itinerary.totalPrice;
  }

  function getIngredientResponse(
    ingredientId: IngredientId
  ): ItineraryMainIngredientResponse | undefined {
    if (!selectedItinerary.value) {
      return;
    }

    let ingredientResponse = selectedItinerary.value.itinerary.find(
      (ingredientResponse) => ingredientResponse.ingredientId === ingredientId
    );

    if (!ingredientResponse) {
      ingredientResponse = [
        ...selectedItinerary.value.hotelOnlyItinerary,
        ...selectedItinerary.value.ownStayOnlyItinerary,
      ].find((ingredientResponse) =>
        ingredientResponse.subIngredientResponse.some(
          (activityIngredientResponse) =>
            activityIngredientResponse.ingredientId === ingredientId
        )
      );
    }

    return ingredientResponse;
  }

  function getOwnStayIngredientResponse(
    ingredientId: IngredientId
  ): OwnStayIngredientResponse | undefined {
    let ingredientResponse = selectedItinerary.value?.ownStayOnlyItinerary.find(
      (ingredientResponse) => ingredientResponse.ingredientId === ingredientId
    );

    if (!ingredientResponse) {
      ingredientResponse = selectedItinerary.value?.ownStayOnlyItinerary.find(
        (ingredientResponse) =>
          ingredientResponse.subIngredientResponse.some(
            (activityIngredientResponse) =>
              activityIngredientResponse.ingredientId === ingredientId
          )
      );
    }

    return ingredientResponse;
  }

  function getHotelIngredientResponse(
    ingredientId: IngredientId
  ): HotelIngredientResponse | undefined {
    let ingredientResponse = selectedItinerary.value?.hotelOnlyItinerary.find(
      (ingredientResponse) => ingredientResponse.ingredientId === ingredientId
    );

    if (!ingredientResponse) {
      ingredientResponse = selectedItinerary.value?.hotelOnlyItinerary.find(
        (ingredientResponse) =>
          ingredientResponse.subIngredientResponse.some(
            (activityIngredientResponse) =>
              activityIngredientResponse.ingredientId === ingredientId
          )
      );
    }

    return ingredientResponse;
  }

  function getTransportIngredientResponse(
    ingredientId: IngredientId
  ): FlightIngredientResponse | FerryIngredientResponse | undefined {
    const ingredientResponse = selectedItinerary.value?.itinerary.find(
      (ingredientResponse) => ingredientResponse.ingredientId === ingredientId
    );
    if (
      ingredientResponse &&
      isTransportIngredientResponse(ingredientResponse)
    ) {
      return ingredientResponse;
    }
  }

  function getFlightIngredientResponse(
    ingredientId: IngredientId
  ): FlightIngredientResponse | undefined {
    const ingredientResponse = selectedItinerary.value?.itinerary.find(
      (ingredientResponse) => ingredientResponse.ingredientId === ingredientId
    );
    if (ingredientResponse && isFlightIngredientResponse(ingredientResponse)) {
      return ingredientResponse;
    }

    return undefined;
  }

  function getFerryIngredientResponse(
    ingredientId: IngredientId
  ): FerryIngredientResponse | undefined {
    const ingredientResponse = selectedItinerary.value?.itinerary.find(
      (ingredientResponse) => ingredientResponse.ingredientId === ingredientId
    );
    if (ingredientResponse && isFerryIngredientResponse(ingredientResponse)) {
      return ingredientResponse;
    }

    return undefined;
  }

  function getIngredientDay(ingredientId: IngredientId): number | undefined {
    const offset = 1;
    if (!selectedItinerary.value) {
      return undefined;
    }
    const ingredient = getIngredientResponse(ingredientId);

    if (!ingredient) {
      return undefined;
    }
    return (
      new SemboDate(ingredient.date).diff(selectedItinerary.value.date, 'day') +
      offset
    );
  }

  function getIngredientDate(ingredientId: IngredientId): Date | undefined {
    const ingredientResponse = getIngredientResponse(ingredientId);

    if (!ingredientResponse) {
      return;
    }

    return ingredientResponse.date;
  }

  function getRangeDays(ingredientId: IngredientId): string {
    if (!selectedItinerary.value) {
      return '';
    }

    const stayOnlyItinerary = selectedItinerary.value.itinerary.filter(
      isStayIngredientResponse
    );
    const positionInItinerary = stayOnlyItinerary.findIndex(
      (ingredientResponse) => ingredientResponse.ingredientId === ingredientId
    );

    if (positionInItinerary === -1) {
      return '';
    }

    const dayText = t('planner.days');

    const startDay =
      stayOnlyItinerary.slice(0, positionInItinerary).reduce((acc, curr) => {
        acc += curr.numberOfNights;
        return acc;
      }, 0) + 1;
    const stayIngredient = stayOnlyItinerary[positionInItinerary];
    assertValue(
      stayIngredient,
      'itineraryStore.ts > getRangeDays > stayIngredient'
    );

    const endDay = startDay + stayIngredient.numberOfNights;

    return `${dayText} ${startDay} - ${endDay}`;
  }

  function getRangeDates(ingredientId: IngredientId): string | undefined {
    if (!selectedItinerary.value) {
      return '';
    }

    const ingredientResponse = getIngredientResponse(ingredientId);

    if (
      !ingredientResponse ||
      !(
        isHotelIngredientResponse(ingredientResponse) ||
        isOwnStayIngredientResponse(ingredientResponse)
      )
    ) {
      return;
    }

    const startDate = new SemboDate(ingredientResponse.date).format(
      DateFormat.DateMonth
    );

    const endDay = new SemboDate(ingredientResponse.date)
      .add({
        days: ingredientResponse.numberOfNights,
      })
      .format(DateFormat.DateMonth);

    return `${startDate} - ${endDay}`;
  }

  function insertActivitySuggestions(
    suggestions: ActivitySuggestionsByIngredientId | null
  ): void {
    if (suggestions) {
      activitySuggestions.value = {
        ...activitySuggestions.value,
        ...suggestions,
      };
    }
  }

  function getActivitySuggestions(
    ingredientId: IngredientId
  ): ActivitySuggestionWithStaticInfo[] {
    return activitySuggestions.value[ingredientId] ?? [];
  }

  function insertDestinationSuggestedActivities(
    suggestedActivities: ActivityStaticInfo[],
    ingredientId: IngredientId
  ): void {
    suggestedActivitiesByDestination.value.set(
      ingredientId,
      suggestedActivities
    );
  }

  function getDestinationSuggestedActivities(
    ingredientId: IngredientId
  ): ActivityStaticInfo[] {
    return suggestedActivitiesByDestination.value.get(ingredientId) ?? [];
  }

  function resetLatestAvailabilitySearchCompletion(generation: number): void {
    latestAvailabilitySearchCompletion.value = {
      generation,
      status: 'pending',
      isCurrentSearchResult: false,
      hasAvailability: false,
      isAvailabilityFailure: false,
    };
  }

  function setLatestAvailabilitySearchCompletion(
    completion: ItineraryAvailabilitySearchCompletion
  ): void {
    if (
      latestAvailabilitySearchCompletion.value &&
      completion.generation <
        latestAvailabilitySearchCompletion.value.generation
    ) {
      return;
    }

    latestAvailabilitySearchCompletion.value = completion;
  }

  function resetStore(): void {
    cheapestItinerary.value = undefined;
    itineraryDates.value = [];
    itinerarySearchCounter.value = 0;
    staticItinerarySearchCounter.value = 0;
    itinerarySearchGeneration.value = 0;
    latestAvailabilitySearchCompletion.value = undefined;
  }

  return {
    itinerarySearchCounter,
    staticItinerarySearchCounter,
    itinerarySearchGeneration,
    latestAvailabilitySearchCompletion,
    isSearchingItineraries,
    isSearchingItinerariesAvailability,
    globalSuggestionItinerariesCounter,
    isSearchingGlobalSuggestionItineraries,
    mostPopularItinerarySuggestionPrice,
    cheapestItinerarySuggestionPrice,

    itineraries,
    cheapestItinerary,
    userSelectedItinerary,
    selectedItinerary,
    tripStartDate,

    itineraryNumberOfPax,
    itineraryNumberOfNights,

    itineraryDates,
    monthIsAvailable,
    dateIsAvailable,
    itinerariesInDateAreNotAvailable,
    itinerariesInMonthAreNotAvailable,
    isNoAvailForMonth,
    areWholeMonthItinerariesSearched,

    insertItinerary,
    insertItinerarySuggestionPrice,
    insertActivitySuggestions,
    clearItineraries,
    clearSingleItinerary,
    toggleItinerary,

    isSelectedItinerary,
    isCheapestItinerary,
    isActiveItinerary,
    isCompleteItinerary,

    getOwnStayIngredientResponse,
    getHotelIngredientResponse,
    getTransportIngredientResponse,
    getFlightIngredientResponse,
    getFerryIngredientResponse,

    getIngredientDay,
    getIngredientDate,
    getRangeDays,
    getRangeDates,
    getActivitySuggestions,

    insertDestinationSuggestedActivities,
    getDestinationSuggestedActivities,
    resetLatestAvailabilitySearchCompletion,
    setLatestAvailabilitySearchCompletion,

    resetStore,
  };
});
